<template>
  <button
    :class="['EdroneTab', tabVariants({ type, active })]"
    :data-e-ga-id="uniqueId"
    :disabled="disabled"
    @click="
      $emit('click');
      uniqueId && gtagUniversalSend({ component_id: uniqueId });
    "
  >
    <span v-if="$slots.icon" class="tw:flex tw:items-center tw:text-[inherit]">
      <!--  @slot This slot is for the icon on the left of the text  -->
      <slot name="icon" />
    </span>
    <span v-if="$slots.default" class="tw:body-1 tw:text-[inherit] tw:truncate tw:leading-[1.125rem]">
      <!--  @slot This slot is for text  -->
      <slot />
    </span>
    <!--  @slot This slot is for badge that appears on the right side of the tab  -->
    <slot name="badge" />
  </button>
</template>

<script setup lang="ts">
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";
import { cva } from "class-variance-authority";

interface Props {
  type?: "underline" | "filled" | "border";
  active?: boolean;
  disabled?: boolean;
  uniqueId?: string;
}

withDefaults(defineProps<Props>(), {
  type: "underline",
  active: false,
  disabled: false,
});

defineEmits(["click"]);

const tabVariants = cva(
  "tw:w-fit tw:sm:min-w-10 tw:flex tw:items-center tw:justify-center tw:gap-2 tw:z-[1] " +
    "tw:text-center tw:whitespace-nowrap tw:cursor-pointer tw:self-end tw:leading-6 tw:text-default-700 " +
    "tw:disabled:pointer-events-none tw:disabled:user-select-none tw:disabled:text-default-500 " +
    "tw:hover:text-black",
  {
    variants: {
      type: {
        underline:
          "tw:h-10 tw:min-h-10 tw:sm:h-14 tw:sm:min-h-14 tw:p-2 tw:sm:p-4 tw:pb-0.5 tw:sm:pb-[0.875rem] tw:bg-transparent tw:border-b tw:border-b-transparent " +
          "tw:transition-[color] tw:ease-out tw:duration-150",
        border:
          "tw:h-10 tw:min-h-10 tw:py-2 tw:px-3 tw:bg-default-000 tw:text-default-700 " +
          "tw:transition-colors tw:ease-out tw:duration-150 " +
          "tw:hover:bg-default-100",
        filled:
          "tw:h-8 tw:min-h-8 tw:min-w-10 tw:py-1 tw:px-2 tw:bg-transparent tw:border tw:border-transparent tw:rounded-sm " +
          "tw:transition-colors tw:ease-out tw:duration-150 " +
          "tw:hover:bg-default-100 tw:disabled:bg-default-000",
      },
      active: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        type: "underline",
        active: true,
        class:
          "tw:!border-b-black tw:!text-black tw:shadow-[0_1px_0_0_#050505] " +
          "tw:disabled:!border-b-default-500 tw:disabled:!text-default-700 tw:disabled:shadow-[0_1px_0_0_#BFBFBF]",
      },
      {
        type: "border",
        active: true,
        class:
          "tw:!text-blue-500 tw:!bg-white tw:shadow-[0_0_15px_0_rgba(0,0,0,0.08)] " +
          "tw:disabled:!bg-default-100 tw:disabled:!text-default-700",
      },
      {
        type: "filled",
        active: true,
        class:
          "tw:!text-blue-500 tw:!border-default-200 tw:!bg-white tw:shadow-[0_0_15px_0_rgba(0,0,0,0.08)] " +
          "tw:disabled:!bg-default-100 tw:disabled:!text-default-700",
      },
    ],
  }
);
</script>
